import React, { useEffect, useState, useRef, MouseEvent, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import {
  Button,
  LinearProgress,
  Tooltip,
  TooltipProps,
  linearProgressClasses,
  tooltipClasses,
} from '@mui/material';
import { styled } from '@mui/system';
import { cloneDeep, isEmpty, round } from 'lodash';

import Color from '@tool/color';
import { CloseIcon20, UpChangeIcon, DownChangeIcon } from '@styled/icon';
import { newRate } from '@tool/index';
import EffectChart from './effect-chart';
import moUser from '@/store/user';
import localforage from 'localforage';
import moIndustry from '../../store/industry';
import { number } from 'mathjs';
import EffectService from '@/service/effect';
import Empty from '@/component/empty';

interface IProps {}
interface IState {}

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    arrow={true}
    classes={{ popper: className }}
    placement="top"/>
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
    padding: '10px 20px',
    '& p': {
      color: Color.white,
      display: 'flex',
      justifyContent: 'space-between',
      width: '200px',
      lineHeight: '34px',
      '& label': {
        fontFamily: 'Mont SemiBold',
      },
      '& span': {
        color: Color.whiteColor(0.7),
      },
    },
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
}));

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 5,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor:
      theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor:
      theme.palette.mode === 'light' ? Color.secondary : Color.ec,
  },
}));

const Effect = (props: IProps, state: IState) => {
  const navi = useNavigate();

  const { effectList } = moIndustry;

  const changeUrl = async (ev: MouseEvent) => {
    ev.stopPropagation();
    if (!moUser.isLogin) return;
    await EffectService.editUserOption({
      my_industry:
        effectList?.cost_per_staff?.industry_lv2 ||
        effectList?.cost_per_staff?.industry_lv1,
    });
    window.location.href = '/effect';
  };

  const goEffect = async (ev: MouseEvent) => {
    ev.stopPropagation();
    if (moUser.isLogin) {
      await EffectService.editUserOption({
        my_industry:
          effectList?.cost_per_staff?.industry_lv2 ||
          effectList?.cost_per_staff?.industry_lv1,
      });
    }
    const humanData: any = (await localforage.getItem('humans')) || {};
    humanData.momenttab = '人均营业收入';
    localforage.setItem('humans', humanData);
    window.location.href = '/effect?key=a';
  };

  const goSalary = async (ev: MouseEvent) => {
    ev.stopPropagation();
    if (moUser.isLogin) {
      await EffectService.editUserOption({
        my_industry:
          effectList?.cost_per_staff?.industry_lv2 ||
          effectList?.cost_per_staff?.industry_lv1,
      });
    }
    const humanData: any = (await localforage.getItem('humans')) || {};
    humanData.industrytab = '人均营业收入';
    localforage.setItem('humans', humanData);
    window.location.href = '/effect?key=b';
  };

  const goConfig = async (ev: MouseEvent) => {
    ev.stopPropagation();
    if (moUser.isLogin) {
      await EffectService.editUserOption({
        my_industry:
          effectList?.staff_size?.industry_lv2 ||
          effectList?.staff_size?.industry_lv1,
      });
    }
    window.location.href = '/effect?key=c';
  };

  const goPotency = async (ev: MouseEvent) => {
    ev.stopPropagation();
    if (moUser.isLogin) {
      await EffectService.editUserOption({
        my_industry:
          effectList?.support_efficiency?.industry_lv2 ||
          effectList?.support_efficiency?.industry_lv1,
      });
    }
    window.location.href = '/effect?key=d';
  };

  const goRank = async (ev: MouseEvent) => {
    ev.stopPropagation();
    if (moUser.isLogin) {
      await EffectService.editUserOption({
        my_industry:
          effectList?.support_efficiency?.industry_lv2 ||
          effectList?.support_efficiency?.industry_lv1,
      });
    }
    window.location.href = '/effect?key=e';
  };

  const getMax = useMemo(() => {
    const result: any = cloneDeep(effectList);
    const list: any = result?.company_rank?.sort(
      (a: any, b: any) => b.value - a.value,
    );
    return !isEmpty(list) ? list[0].value : 100;
  }, [effectList]);

  useEffect(() => {
    return () => {};
  }, []);

  return (
    <section className="dash-effect-warp" style={{ marginTop: '10px' }}>
      <section className="dash-industry-content">
        <h3 className="top">人效</h3>
        <section className="contents">
          <section className="left dash-move" onClick={goEffect}>
            <h4 style={{ color: Color.primary }}>人效（万元）</h4>
            <EffectChart />
          </section>
          <section className="mid dash-move" onClick={goSalary}>
            <h4>
              人均薪酬成本（元）
              {/*&nbsp;<span>new</span> */}
            </h4>
            <HtmlTooltip
              title={
                <React.Fragment>
                  <p>
                    <span>行业</span>
                    <b>
                      {effectList?.cost_per_staff?.industry_lv2 ||
                        effectList?.cost_per_staff?.industry_lv1}
                    </b>
                  </p>
                  <p>
                    <span>时间</span>
                    <label>{effectList?.cost_per_staff?.year}</label>
                  </p>
                </React.Fragment>
              }>
              <b>
                {effectList?.cost_per_staff?.p50 ||
                effectList?.cost_per_staff?.p50 === 0 ?
                  round(effectList?.cost_per_staff?.p50, 0).toLocaleString() :
                  '-'}
              </b>
            </HtmlTooltip>
            <section className="change-radio">
              <a>较上期变化</a>
              {effectList?.cost_per_staff?.p50_ratio ||
            effectList?.cost_per_staff?.p50_ratio === 0 ? (
                  <span>
                    {effectList?.cost_per_staff?.p50_ratio >= 0 ? (
                      <UpChangeIcon />
                    ) : (
                      <DownChangeIcon />
                    )}
                &nbsp;
                    <i
                      style={{
                        color:
                      effectList?.cost_per_staff?.p50_ratio >= 0 ?
                        Color.red :
                        Color.success,
                      }}>
                      {round(
                        Math.abs(effectList?.cost_per_staff?.p50_ratio) * 100,
                        1,
                      )}
                  %
                    </i>
                  </span> ) : '-'}
            </section>

          </section>
          <section className="mid dash-move" onClick={goConfig}>
            <h4>
              人员配置（人）
              {/* &nbsp;<span>new</span> */}
            </h4>
            <HtmlTooltip
              title={
                <React.Fragment>
                  <p>
                    <span>行业</span>
                    <b>
                      {effectList?.staff_size?.industry_lv2 ||
                        effectList?.staff_size?.industry_lv1}
                    </b>
                  </p>
                  <p>
                    <span>时间</span>
                    <label>{effectList?.staff_size?.year}</label>
                  </p>
                </React.Fragment>
              }>
              <b>
                {effectList?.staff_size?.p50 ||
                effectList?.staff_size?.p50 === 0 ?
                  effectList?.staff_size?.p50.toLocaleString() :
                  '-'}
              </b>
            </HtmlTooltip>

            <section className="change-radio">
              <a>较上期变化</a>
              {effectList?.staff_size?.p50_ratio ||
            effectList?.staff_size?.p50_ratio === 0 ? (<span>
                  {effectList?.staff_size?.p50_ratio >= 0 ? (
                    <UpChangeIcon />
                  ) : (
                    <DownChangeIcon />
                  )}
                &nbsp;
                  <i
                    style={{
                      color:
                      effectList?.staff_size?.p50_ratio >= 0 ?
                        Color.red :
                        Color.success,
                    }}>
                    {round(Math.abs(effectList?.staff_size?.p50_ratio) * 100, 1)}%
                  </i>
                </span> ) : '-'}
            </section>
          </section>
          <section className="mid dash-move" onClick={goPotency}>
            <h4>
              支持效能
              {/* &nbsp;<span>new</span> */}
            </h4>
            <HtmlTooltip
              title={
                <React.Fragment>
                  <p>
                    <span>行业</span>
                    <b>
                      {effectList?.support_efficiency?.industry_lv2 ||
                        effectList?.support_efficiency?.industry_lv1}
                    </b>
                  </p>
                  <p>
                    <span>时间</span>
                    <label>{effectList?.support_efficiency?.year}</label>
                  </p>
                  <p>
                    <span>全职员工数</span>
                    <label>1000</label>
                  </p>
                </React.Fragment>
              }>
              <b>
                {effectList?.support_efficiency?.hr ||
                effectList?.support_efficiency?.hr === 0 ?
                  Math.ceil(
                    effectList?.support_efficiency?.hr.toLocaleString(),
                  ) :
                  '-'}
              </b>
            </HtmlTooltip>
            <section className="change-radio">
              <a>人力资源</a>
              {/* <a>较上期变化</a>
              <UpChangeIcon />
              <DownChangeIcon />
              &nbsp;
              <i>3.39%</i> */}
            </section>
          </section>
          <section className="right dash-move common-progress" onClick={goRank}>
            <h4>
              企业人效排行
              {/* &nbsp;<span>new</span> */}
            </h4>
            {!isEmpty(effectList?.company_rank) ? (
              <section style={{ width:'100%' }}>
                {effectList?.company_rank?.map((res: any, r: any) => {
                  return (
                    <section key={r} className="progress-item">
                      <b>{r + 1}</b>
                      <section className="liner-show">
                        <p>{res.company_name}</p>
                        <BorderLinearProgress
                          variant="determinate"
                          value={(res.value / getMax) * 100}/>
                      </section>
                      <span className="numbers">
                        {res.value ? round(res.value, 0).toLocaleString() : ''}
                      </span>
                    </section>
                  );
                })}
              </section>
            ) : (
              <Empty height="auto" />
            )}
          </section>
        </section>
      </section>
      {!moUser.isLogin && (
        <section className="position-title">
          其他行业数据 - 登录体验更细分维度数据
        </section>
      )}
      {moUser.isEfficiencyExpired && (
        <section className="position-title">
          其他行业数据 - 开通会员，支持所有细分行业数据
        </section>
      )}
    </section>
  );
};

export default observer(Effect);
